import { useEffect, useMemo, useState, useRef, useCallback } from 'react';

// Performance constants
const HIGH_FREQUENCY_THRESHOLD = 100; // ms
const DEBOUNCE_DELAY = 50; // ms for rapid prop changes
const DEFAULT_INTERVAL = 1000; // 1 second
const MINUTE_INTERVAL = 60000; // 1 minute

// TypeScript interfaces for better type safety
export interface TimerConfig {
  interval?: number;
  precision?: 'millisecond' | 'second' | 'minute' | 'hour';
  highPerformance?: boolean;
  batteryOptimized?: boolean;
  paused?: boolean;
  timezone?: string;
}

export interface ElapsedTimeConfig extends TimerConfig {
  startTime: Date | string | number;
}

export interface CountdownConfig extends TimerConfig {
  endTime: Date | string | number;
  onComplete?: () => void;
}

export interface CurrentTimeConfig extends TimerConfig {
  // No additional properties needed
}

export interface ScheduledTimeConfig extends TimerConfig {
  startTime: Date | string | number;
}

// Utility functions
export function sanitizeToDate(value: string | number | Date): Date | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  const parsed = Date.parse(value);
  return isNaN(parsed) ? null : new Date(parsed);
}

export function getIntervalFromPrecision(precision: TimerConfig['precision']): number {
  switch (precision) {
    case 'millisecond':
      return 16; // ~60fps
    case 'second':
      return 1000;
    case 'minute':
      return 60000;
    case 'hour':
      return 3600000;
    default:
      return DEFAULT_INTERVAL;
  }
}

export function applyTimezone(date: Date, timezone?: string): Date {
  if (!timezone) return date;

  try {
    // Create a new date in the specified timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const partsObj = parts.reduce(
      (acc, part) => {
        acc[part.type] = part.value;
        return acc;
      },
      {} as Record<string, string>
    );

    return new Date(
      parseInt(partsObj.year),
      parseInt(partsObj.month) - 1,
      parseInt(partsObj.day),
      parseInt(partsObj.hour),
      parseInt(partsObj.minute),
      parseInt(partsObj.second)
    );
  } catch (error) {
    console.warn(`Invalid timezone: ${timezone}, falling back to local time`, error);
    return date;
  }
}

// Core timer hook with all optimizations
function useOptimizedTimer(initialTime: Date, config: TimerConfig, updateFunction: (currentTime: Date) => Date): Date {
  const {
    interval = DEFAULT_INTERVAL,
    precision,
    highPerformance = false,
    batteryOptimized = false,
    paused = false,
    timezone,
  } = config;

  // Calculate effective interval
  const effectiveInterval = useMemo(() => {
    if (precision) return getIntervalFromPrecision(precision);
    if (batteryOptimized) return Math.max(interval, MINUTE_INTERVAL);
    if (highPerformance) return Math.min(interval, 16);
    return interval;
  }, [interval, precision, batteryOptimized, highPerformance]);

  // Refs for performance optimization
  const timeRef = useRef<Date>(initialTime);
  const timerRef = useRef<number | undefined>();
  const animationFrameRef = useRef<number>();
  const debounceRef = useRef<number | undefined>();
  const isActiveRef = useRef(true);
  const lastConfigRef = useRef<string>();

  // State for the current time
  const [time, setTime] = useState(() => {
    const adjustedTime = timezone ? applyTimezone(initialTime, timezone) : initialTime;
    timeRef.current = adjustedTime;
    return adjustedTime;
  });

  // Optimized update function
  const updateTime = useCallback(() => {
    if (!isActiveRef.current || paused) return;

    const newTime = updateFunction(timeRef.current);
    const adjustedTime = timezone ? applyTimezone(newTime, timezone) : newTime;

    timeRef.current = adjustedTime;
    setTime(adjustedTime);
  }, [updateFunction, timezone, paused]);

  // Cleanup function
  const clearAllTimers = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
  }, []);

  // Setup timer function
  const setupTimer = useCallback(() => {
    if (!isActiveRef.current || paused) return;

    clearAllTimers();

    // Choose optimal timer strategy
    if (effectiveInterval < HIGH_FREQUENCY_THRESHOLD || highPerformance) {
      // Use requestAnimationFrame for high-frequency updates
      const animationLoop = () => {
        if (!isActiveRef.current || paused) return;
        updateTime();
        animationFrameRef.current = requestAnimationFrame(animationLoop);
      };
      animationFrameRef.current = requestAnimationFrame(animationLoop);
    } else {
      // Use setTimeout for lower frequency updates
      const setupRecurringTimer = () => {
        if (!isActiveRef.current || paused) return;

        updateTime();
        timerRef.current = window.setTimeout(setupRecurringTimer, effectiveInterval);
      };

      timerRef.current = window.setTimeout(setupRecurringTimer, effectiveInterval);
    }
  }, [effectiveInterval, highPerformance, paused, updateTime, clearAllTimers]);

  // Effect for configuration changes with debouncing
  useEffect(() => {
    const configHash = JSON.stringify({ effectiveInterval, highPerformance, paused, timezone });

    // Skip if configuration hasn't changed
    if (lastConfigRef.current === configHash) return;

    lastConfigRef.current = configHash;
    isActiveRef.current = true;

    // Debounce rapid configuration changes
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }

    debounceRef.current = window.setTimeout(() => {
      setupTimer();
    }, DEBOUNCE_DELAY);

    return () => {
      isActiveRef.current = false;
      clearAllTimers();
    };
  }, [effectiveInterval, highPerformance, paused, timezone, setupTimer, clearAllTimers]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      isActiveRef.current = false;
      clearAllTimers();
    },
    [clearAllTimers]
  );

  return time;
}

/**
 * Hook for tracking elapsed time since a start time
 * Perfect for timers, stopwatches, and duration tracking
 */
export function useElapsedTime(config: ElapsedTimeConfig): {
  elapsedTime: Date;
  elapsedSeconds: number;
  elapsedMinutes: number;
  elapsedHours: number;
} {
  const startTime = useMemo(() => {
    const sanitized = sanitizeToDate(config.startTime);
    return sanitized || new Date();
  }, [config.startTime]);

  const elapsedTime = useOptimizedTimer(
    startTime,
    config,
    useCallback(() => new Date(), [])
  );

  const elapsedSeconds = Math.floor((elapsedTime.getTime() - startTime.getTime()) / 1000);
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  const elapsedHours = Math.floor(elapsedMinutes / 60);

  return {
    elapsedTime,
    elapsedSeconds,
    elapsedMinutes,
    elapsedHours,
  };
}

/**
 * Hook for countdown timers
 * Perfect for countdowns, deadlines, and time-limited operations
 */
export function useCountdown(config: CountdownConfig): {
  currentTime: Date;
  remainingSeconds: number;
  remainingMinutes: number;
  remainingHours: number;
  isExpired: boolean;
} {
  const endTime = useMemo(() => {
    const sanitized = sanitizeToDate(config.endTime);
    return sanitized || new Date();
  }, [config.endTime]);

  const currentTime = useOptimizedTimer(
    new Date(),
    config,
    useCallback(() => new Date(), [])
  );

  const remainingMs = Math.max(0, endTime.getTime() - currentTime.getTime());
  const remainingSeconds = Math.floor(remainingMs / 1000);
  const remainingMinutes = Math.floor(remainingSeconds / 60);
  const remainingHours = Math.floor(remainingMinutes / 60);
  const isExpired = remainingMs <= 0;

  // Call onComplete when countdown expires
  const { onComplete } = config;
  useEffect(() => {
    if (isExpired && onComplete) {
      onComplete();
    }
  }, [isExpired, onComplete]);

  return {
    currentTime,
    remainingSeconds,
    remainingMinutes,
    remainingHours,
    isExpired,
  };
}

/**
 * Hook for real-time clock display
 * Perfect for wall clocks, timestamps, and current time display
 */
export function useCurrentTime(config: CurrentTimeConfig = {}): Date {
  return useOptimizedTimer(
    new Date(),
    { interval: DEFAULT_INTERVAL, ...config },
    useCallback(() => new Date(), [])
  );
}

/**
 * Improved useTimeOnSchedule hook with better API and configuration
 * Maintains backward compatibility while adding new features
 */
export function useTimeOnSchedule(
  start: string | number | Date | ScheduledTimeConfig,
  intervalOrConfig?: number | ScheduledTimeConfig
): Date {
  // Handle backward compatibility
  const config: ScheduledTimeConfig = useMemo(() => {
    if (typeof start === 'object' && 'startTime' in start) {
      // New API: useTimeOnSchedule(config)
      return start;
    } else {
      // Old API: useTimeOnSchedule(start, interval)
      const interval = typeof intervalOrConfig === 'number' ? intervalOrConfig : DEFAULT_INTERVAL;
      const additionalConfig = typeof intervalOrConfig === 'object' ? intervalOrConfig : {};

      return {
        startTime: start as string | number | Date,
        interval,
        ...additionalConfig,
      };
    }
  }, [start, intervalOrConfig]);

  const startTime = useMemo(() => {
    const sanitized = sanitizeToDate(config.startTime);
    return sanitized?.getTime() ?? Date.now();
  }, [config.startTime]);

  return useOptimizedTimer(
    new Date(startTime),
    config,
    useCallback(() => {
      const now = Date.now();
      const intervalsPassed = Math.floor((now - startTime) / (config.interval || DEFAULT_INTERVAL));
      const scheduledTime = startTime + intervalsPassed * (config.interval || DEFAULT_INTERVAL);
      return new Date(scheduledTime);
    }, [startTime, config.interval])
  );
}

// Export the original function for backward compatibility
export default useTimeOnSchedule;
